import React, { useState, useEffect, useCallback } from "react";
import { snapshotsApi, statsApi } from "../lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, ArrowClockwise, Funnel, Bookmark, X, SlidersHorizontal } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import RatingOvals from "../components/RatingOvals";
import IntelligenceWorkbench from "../components/workbench/IntelligenceWorkbench";

// ── Design tokens ─────────────────────────────────────────────────────────────
const SIGNAL_BORDER = {
  trend:       "border-l-teal-500",
  opportunity: "border-l-emerald-500",
  anomaly:     "border-l-amber-500",
  contrarian:  "border-l-rose-500",
};

const CONFIDENCE_OPTIONS = [
  { label: "Any confidence", value: 0 },
  { label: "≥ 60",           value: 60 },
  { label: "≥ 70",           value: 70 },
  { label: "≥ 80",           value: 80 },
  { label: "≥ 90",           value: 90 },
];

const DATE_OPTIONS = [
  { label: "All time",   value: null },
  { label: "Last 7 days",  value: 7 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
];

const SORT_OPTIONS = [
  { label: "Newest first",    value: "newest" },
  { label: "Highest confidence", value: "confidence" },
];

// ── Skeleton card ─────────────────────────────────────────────────────────────
function SignalCardSkeleton() {
  return (
    <div className="border border-slate-200 border-l-4 border-l-slate-200 rounded-lg bg-white p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Skeleton className="h-5 w-20 rounded-md" />
        <Skeleton className="h-5 w-14 rounded-md" />
        <Skeleton className="h-5 w-10 ml-auto" />
      </div>
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-4 w-3/5" />
      <div className="flex gap-2 pt-1">
        <Skeleton className="h-4 w-16 rounded-full" />
        <Skeleton className="h-4 w-16 rounded-full" />
        <Skeleton className="h-1.5 w-full ml-auto rounded-full" />
      </div>
    </div>
  );
}

// ── Stat tile ─────────────────────────────────────────────────────────────────
function StatTile({ label, value, accent }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</span>
      <span className={`font-mono text-[22px] font-bold leading-none tabular-nums ${accent || "text-slate-900"}`}>
        {value ?? <Skeleton className="h-6 w-12 inline-block" />}
      </span>
    </div>
  );
}

// ── Signal card ───────────────────────────────────────────────────────────────
function SignalCard({ snap, onOpen, onBookmarkToggle, bookmarked, onRate, index }) {
  const border    = SIGNAL_BORDER[snap.signal_type] || SIGNAL_BORDER.opportunity;
  const confColor = snap.confidence_score >= 80 ? "bg-emerald-500"
                  : snap.confidence_score >= 60 ? "bg-amber-500" : "bg-rose-500";
  const confText  = snap.confidence_score >= 80 ? "text-emerald-600"
                  : snap.confidence_score >= 60 ? "text-amber-600" : "text-rose-600";

  return (
    <article
      className={`group relative bg-white border border-slate-200 border-l-4 ${border} rounded-lg cursor-pointer hover:shadow-md hover:border-slate-300 transition-all duration-150 fade-in`}
      style={{ animationDelay: `${Math.min(index, 10) * 35}ms`, animationFillMode: "both" }}
      onClick={() => onOpen(snap)}
    >
      <div className="px-4 pt-4 pb-3">
        {/* Top row */}
        <div className="flex items-center gap-2 mb-3">
          <Badge variant={snap.signal_type || "opportunity"}>
            {(snap.signal_type || "opportunity").toUpperCase()}
          </Badge>
          {snap.epistemic_status && snap.epistemic_status !== "insight" && (
            <Badge variant={snap.epistemic_status}>
              {snap.epistemic_status.charAt(0).toUpperCase() + snap.epistemic_status.slice(1)}
            </Badge>
          )}
          {snap.geo && snap.geo !== "Global" && (
            <span className="text-[11px] font-mono text-slate-400 uppercase tracking-wider">{snap.geo}</span>
          )}
          <div className="ml-auto flex items-center gap-2.5">
            <span className={`font-mono text-[14px] font-bold tabular-nums ${confText}`}>
              {snap.confidence_score}<span className="text-[10px] font-normal text-slate-400">/100</span>
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onBookmarkToggle(snap); }}
              className="p-1 rounded hover:bg-slate-100 transition-colors"
              title={bookmarked ? "Remove from vault" : "Save to vault"}
            >
              <Bookmark
                size={15}
                weight={bookmarked ? "fill" : "regular"}
                className={bookmarked ? "text-slate-700" : "text-slate-400 group-hover:text-slate-500"}
              />
            </button>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-[15px] font-semibold text-slate-900 leading-snug mb-2">{snap.title}</h3>

        {/* Summary */}
        <p className="text-[13px] text-slate-600 leading-relaxed line-clamp-2 mb-3">{snap.summary}</p>

        {/* Source prompt */}
        {snap.source_prompt && (
          <div className="flex items-center gap-1.5 mb-2.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">From:</span>
            <span className="text-[11px] text-slate-500 italic truncate">{snap.source_prompt}</span>
          </div>
        )}

        {/* Tags + date */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {(snap.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="text-[11px] font-mono uppercase tracking-wider text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
              {t}
            </span>
          ))}
          <span className="ml-auto text-[11px] font-mono text-slate-400">
            {new Date(snap.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
          </span>
        </div>

        {/* Footer: rating + confidence bar */}
        <div className="flex items-center gap-3 mt-1">
          <RatingOvals
            value={snap.operator_rating}
            onChange={(r) => onRate(snap.id, r)}
            compact
          />
          <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${confColor}`} style={{ width: `${snap.confidence_score}%` }} />
          </div>
        </div>
      </div>
    </article>
  );
}

// ── Select helper ─────────────────────────────────────────────────────────────
function FilterSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value === "null" ? null : isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value))}
      className="text-[12px] text-slate-600 border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-slate-200 hover:border-slate-300 transition-colors cursor-pointer"
    >
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
      ))}
    </select>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function TrendRadarPage() {
  const [snapshots,     setSnapshots]     = useState([]);
  const [stats,         setStats]         = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [signalFilter,  setSignalFilter]  = useState("all");
  const [confidenceMin, setConfidenceMin] = useState(0);
  const [days,          setDays]          = useState(null);
  const [sortBy,        setSortBy]        = useState("newest");
  const [searchQ,       setSearchQ]       = useState("");
  const [searchInput,   setSearchInput]   = useState("");
  const [workbenchId,   setWorkbenchId]   = useState(null);
  const [bookmarks,     setBookmarks]     = useState(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: sortBy };
      if (signalFilter !== "all") params.signal_type = signalFilter;
      if (confidenceMin > 0)      params.confidence_min = confidenceMin;
      if (days)                   params.days = days;
      if (searchQ)                params.q = searchQ;

      const [snapsRes, statsRes] = await Promise.all([
        snapshotsApi.list(params),
        statsApi.get(),
      ]);
      setSnapshots(snapsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load intelligence feed");
    } finally {
      setLoading(false);
    }
  }, [signalFilter, confidenceMin, days, sortBy, searchQ]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("workbench");
    if (id) setWorkbenchId(id);
  }, []);

  const openWorkbench = (snap) => {
    setWorkbenchId(snap.id);
    const url = new URL(window.location);
    url.searchParams.set("workbench", snap.id);
    window.history.pushState({}, "", url);
  };

  const closeWorkbench = () => {
    setWorkbenchId(null);
    const url = new URL(window.location);
    url.searchParams.delete("workbench");
    window.history.pushState({}, "", url);
  };

  const handleRate = async (id, rating) => {
    setSnapshots((s) => s.map((x) => x.id === id ? { ...x, operator_rating: rating } : x));
    try {
      await snapshotsApi.rate(id, { rating });
    } catch {
      toast.error("Failed to save rating");
      setSnapshots((s) => s.map((x) => x.id === id ? { ...x, operator_rating: snapshots.find(s => s.id === id)?.operator_rating } : x));
    }
  };

  const handleBookmarkToggle = async (snap) => {
    const wasBookmarked = bookmarks.has(snap.id);
    const next = new Set(bookmarks);
    if (wasBookmarked) {
      next.delete(snap.id);
      try {
        const { bookmarksApi } = await import("../lib/api");
        await bookmarksApi.remove(snap.id);
        toast.success("Removed from vault");
      } catch { next.add(snap.id); toast.error("Failed"); }
    } else {
      next.add(snap.id);
      try {
        const { bookmarksApi } = await import("../lib/api");
        await bookmarksApi.add(snap.id);
        toast.success("Saved to vault");
      } catch { next.delete(snap.id); toast.error("Failed"); }
    }
    setBookmarks(next);
  };

  const hasActiveFilters = signalFilter !== "all" || confidenceMin > 0 || days !== null || searchQ;

  const clearFilters = () => {
    setSignalFilter("all");
    setConfidenceMin(0);
    setDays(null);
    setSearchQ("");
    setSearchInput("");
    setSortBy("newest");
  };

  return (
    <div className="flex flex-col h-full">
      {/* ── Stats bar ─────────────────────────────────────────────────────── */}
      <div className="border-b border-slate-200 bg-white px-6 py-5 shrink-0">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1">Trend Radar</p>
            <h1 className="font-display text-[22px] font-bold text-slate-900 leading-tight tracking-tight">
              Intelligence Feed
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">
              All signals from Hermes. Filter, review, and save what matters to your Vault.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            <ArrowClockwise size={13} className={loading ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>

        <div className="flex gap-8 pt-4 border-t border-slate-100 flex-wrap">
          <StatTile label="Signals"      value={stats?.total_insights} />
          <StatTile label="Opportunities"value={stats?.opportunities}  accent="text-emerald-600" />
          <StatTile label="Trends"       value={stats?.trends}         accent="text-teal-600" />
          <StatTile label="Anomalies"    value={stats?.anomalies}      accent="text-amber-600" />
          <StatTile label="Avg Confidence"
            value={stats ? `${Math.round(stats.avg_confidence || 0)}` : undefined}
            accent={stats?.avg_confidence >= 70 ? "text-emerald-600" : "text-amber-600"}
          />
          <StatTile label="Sources"   value={stats?.sources_tracked} />
          <StatTile label="Learnings" value={stats?.learnings} accent="text-emerald-600" />
        </div>
      </div>

      {/* ── Filter bar ────────────────────────────────────────────────────── */}
      <div className="border-b border-slate-200 bg-white px-6 py-2.5 flex items-center gap-3 flex-wrap shrink-0">
        {/* Signal type tabs */}
        <Tabs value={signalFilter} onValueChange={(v) => { setSignalFilter(v); setSearchQ(""); setSearchInput(""); }}>
          <TabsList className="bg-slate-100 h-8">
            {[
              { value: "all",         label: "All" },
              { value: "opportunity", label: "Opportunities" },
              { value: "trend",       label: "Trends" },
              { value: "anomaly",     label: "Anomalies" },
              { value: "contrarian",  label: "Contrarian" },
            ].map((f) => (
              <TabsTrigger key={f.value} value={f.value}
                className="text-[12px] h-6 px-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="h-4 w-px bg-slate-200" />

        {/* Confidence */}
        <FilterSelect
          value={confidenceMin}
          onChange={setConfidenceMin}
          options={CONFIDENCE_OPTIONS}
        />

        {/* Date range */}
        <FilterSelect
          value={String(days)}
          onChange={setDays}
          options={DATE_OPTIONS}
        />

        {/* Sort */}
        <FilterSelect
          value={sortBy}
          onChange={setSortBy}
          options={SORT_OPTIONS}
        />

        <div className="h-4 w-px bg-slate-200" />

        {/* Search */}
        <form
          onSubmit={(e) => { e.preventDefault(); setSearchQ(searchInput.trim()); }}
          className="flex items-center gap-2 flex-1 max-w-64"
        >
          <div className="relative flex-1">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search signals…"
              className="w-full pl-7 pr-3 py-1.5 text-[13px] border border-slate-200 rounded-md bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>
          {searchQ && (
            <button type="button" onClick={() => { setSearchQ(""); setSearchInput(""); }}
              className="text-slate-400 hover:text-slate-600">
              <X size={14} />
            </button>
          )}
        </form>

        <div className="ml-auto flex items-center gap-3">
          {hasActiveFilters && (
            <button onClick={clearFilters}
              className="text-[12px] text-slate-500 hover:text-slate-800 flex items-center gap-1 transition-colors">
              <X size={12} /> Clear filters
            </button>
          )}
          <span className="text-[11px] font-mono text-slate-400">
            {snapshots.length} signal{snapshots.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* ── Feed ──────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto bg-slate-50/50">
        {loading ? (
          <div className="p-6 grid gap-3 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 auto-rows-min">
            {Array.from({ length: 6 }).map((_, i) => <SignalCardSkeleton key={i} />)}
          </div>
        ) : snapshots.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Funnel size={32} className="text-slate-300" />
            <p className="text-[15px] font-medium text-slate-500">No signals match these filters.</p>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <X size={12} /> Clear filters
            </Button>
          </div>
        ) : (
          <div className="p-6 grid gap-3 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 auto-rows-min">
            {snapshots.map((snap, i) => (
              <SignalCard
                key={snap.id}
                snap={snap}
                index={i}
                onOpen={openWorkbench}
                bookmarked={bookmarks.has(snap.id)}
                onBookmarkToggle={handleBookmarkToggle}
                onRate={handleRate}
              />
            ))}
          </div>
        )}
      </div>

      {workbenchId && (
        <IntelligenceWorkbench
          snapshotId={workbenchId}
          onClose={closeWorkbench}
          onBookmarkToggle={handleBookmarkToggle}
          bookmarked={bookmarks.has(workbenchId)}
        />
      )}
    </div>
  );
}
